/**
 * Critical Workflow Provider
 * 
 * React Context Provider that integrates WorkflowCriticalityManager with the workflow system.
 * Provides hooks for managing critical task execution, checkout/release, and offline queuing.
 */

import React, { createContext, useContext, useEffect, useState, useCallback, ReactNode } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { WorkflowCriticalityManager } from './WorkflowCriticalityManager';
import { useAuth } from '@ferp/auth';

interface CriticalWorkflowContextValue {
  manager: WorkflowCriticalityManager | null;
  isOnline: boolean;
  
  // Task Management
  canExecuteOffline: (taskId: string, workflowType: string, taskType?: string) => {
    canExecute: boolean;
    reason?: string;
    maxOfflineHours?: number;
  };
  
  checkoutTask: (
    taskId: string,
    instanceId: string,
    workflowType: string,
    taskType?: string
  ) => Promise<{ success: boolean; checkoutId?: string; error?: string }>;
  
  releaseTask: (taskId: string, checkoutId?: string) => Promise<boolean>;
  
  executeCriticalTask: (
    taskId: string,
    instanceId: string,
    workflowType: string,
    taskType: string,
    taskData: Record<string, unknown>,
    executeCallback: () => Promise<any>
  ) => Promise<{ success: boolean; result?: any; error?: string; queued?: boolean }>;
  
  // Queue Management
  processOfflineQueue: () => Promise<{ processed: number; failed: number }>;
  getQueueStats: () => { critical: number; sensitive: number; total: number };
  
  // Lock Status
  getActiveLocks: () => Array<{
    taskId: string;
    instanceId: string;
    workflowType: string;
    lockedBy: string;
    lockedAt: number;
    expiresAt: number;
  }>;
  
  // Workflow Classification
  classifyWorkflow: (workflowType: string, taskType?: string) => {
    id: string;
    name: string;
    level: 'critical' | 'sensitive' | 'standard';
    requiresConnection: boolean;
    maxOfflineHours: number;
    preventDuplicates: boolean;
  } | null;
}

const CriticalWorkflowContext = createContext<CriticalWorkflowContextValue | null>(null);

interface CriticalWorkflowProviderProps {
  children: ReactNode;
  deviceId?: string;
  sessionId?: string;
}

export const CriticalWorkflowProvider: React.FC<CriticalWorkflowProviderProps> = ({
  children,
  deviceId = generateDeviceId(),
  sessionId = generateSessionId()
}) => {
  const { user } = useAuth();
  const apolloClient = useApolloClient();
  const [manager, setManager] = useState<WorkflowCriticalityManager | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueStats, setQueueStats] = useState({ critical: 0, sensitive: 0, total: 0 });

  // Initialize manager when user is available
  useEffect(() => {
    if (user?.id) {
      const criticalityManager = new WorkflowCriticalityManager(
        user.id,
        deviceId,
        sessionId,
        isOnline,
        apolloClient
      );
      setManager(criticalityManager);
    }
  }, [user?.id, deviceId, sessionId, isOnline, apolloClient]);

  // Handle online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (manager) {
        manager.processOfflineQueue().then(() => {
          setQueueStats(manager.getQueueStats());
        });
      }
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [manager]);

  // Update queue stats periodically
  useEffect(() => {
    if (!manager) return undefined;

    const updateStats = () => {
      setQueueStats(manager.getQueueStats());
    };

    updateStats();
    const interval = setInterval(updateStats, 30000); // Every 30 seconds

    return () => clearInterval(interval);
  }, [manager]);

  // Wrapper methods that check manager availability
  const canExecuteOffline = useCallback((
    taskId: string,
    workflowType: string,
    taskType?: string
  ) => {
    if (!manager) {
      return { canExecute: false, reason: 'Criticality manager not initialized' };
    }
    return manager.canExecuteOffline(taskId, workflowType, taskType);
  }, [manager]);

  const checkoutTask = useCallback(async (
    taskId: string,
    instanceId: string,
    workflowType: string,
    taskType?: string
  ) => {
    if (!manager) {
      return { success: false, error: 'Criticality manager not initialized' };
    }
    return manager.checkoutTask(taskId, instanceId, workflowType, taskType);
  }, [manager]);

  const releaseTask = useCallback(async (taskId: string, checkoutId?: string) => {
    if (!manager) return false;
    return manager.releaseTask(taskId, checkoutId);
  }, [manager]);

  const executeCriticalTask = useCallback(async (
    taskId: string,
    instanceId: string,
    workflowType: string,
    taskType: string,
    taskData: Record<string, unknown>,
    executeCallback: () => Promise<any>
  ) => {
    if (!manager) {
      return { success: false, error: 'Criticality manager not initialized' };
    }
    
    const result = await manager.executeCriticalTask(
      taskId,
      instanceId,
      workflowType,
      taskType,
      taskData,
      executeCallback
    );
    
    // Update queue stats after execution
    setQueueStats(manager.getQueueStats());
    
    return result;
  }, [manager]);

  const processOfflineQueue = useCallback(async () => {
    if (!manager) return { processed: 0, failed: 0 };
    
    const result = await manager.processOfflineQueue();
    setQueueStats(manager.getQueueStats());
    
    return result;
  }, [manager]);

  const getQueueStats = useCallback(() => {
    return queueStats;
  }, [queueStats]);

  const getActiveLocks = useCallback(() => {
    if (!manager) return [];
    return manager.getActiveLocks();
  }, [manager]);

  const classifyWorkflow = useCallback((workflowType: string, taskType?: string) => {
    if (!manager) return null;
    return manager.classifyWorkflow(workflowType, taskType);
  }, [manager]);

  const contextValue: CriticalWorkflowContextValue = {
    manager,
    isOnline,
    canExecuteOffline,
    checkoutTask,
    releaseTask,
    executeCriticalTask,
    processOfflineQueue,
    getQueueStats,
    getActiveLocks,
    classifyWorkflow
  };

  return (
    <CriticalWorkflowContext.Provider value={contextValue}>
      {children}
    </CriticalWorkflowContext.Provider>
  );
};

export const useCriticalWorkflow = (): CriticalWorkflowContextValue => {
  const context = useContext(CriticalWorkflowContext);
  if (!context) {
    throw new Error('useCriticalWorkflow must be used within a CriticalWorkflowProvider');
  }
  return context;
};

// Specialized hook for medication administration
export const useMedicationSafety = () => {
  const { checkoutTask, releaseTask, executeCriticalTask, classifyWorkflow } = useCriticalWorkflow();

  const administeredMedications = useCallback(async (
    medicationId: string,
    patientId: string,
    dosage: string,
    medicationData: Record<string, unknown>
  ) => {
    const taskId = `med_admin_${medicationId}_${patientId}`;
    const instanceId = `med_instance_${Date.now()}`;

    return executeCriticalTask(
      taskId,
      instanceId,
      'MEDICATION_ADMIN',
      'ADMINISTER_DRUG',
      { medicationId, patientId, dosage, ...medicationData },
      async () => {
        // Actual medication administration logic would go here
        // This is where you'd call your backend API to record the administration
        console.log('Administering medication:', { medicationId, patientId, dosage });
        return { administered: true, timestamp: new Date().toISOString() };
      }
    );
  }, [executeCriticalTask]);

  const isMedicationCritical = useCallback((workflowType: string) => {
    const classification = classifyWorkflow(workflowType);
    return classification?.level === 'critical' && classification.name.includes('Medication');
  }, [classifyWorkflow]);

  return {
    administeredMedications,
    isMedicationCritical,
    checkoutMedicationTask: checkoutTask,
    releaseMedicationTask: releaseTask
  };
};

// Specialized hook for financial transactions
export const useFinancialSafety = () => {
  const { checkoutTask, releaseTask, executeCriticalTask, classifyWorkflow } = useCriticalWorkflow();

  const processPayment = useCallback(async (
    paymentId: string,
    amount: number,
    paymentData: Record<string, unknown>
  ) => {
    const taskId = `payment_${paymentId}`;
    const instanceId = `payment_instance_${Date.now()}`;

    return executeCriticalTask(
      taskId,
      instanceId,
      'BILL_PAYMENT',
      'PROCESS_PAYMENT',
      { paymentId, amount, ...paymentData },
      async () => {
        // Actual payment processing logic would go here
        console.log('Processing payment:', { paymentId, amount });
        return { processed: true, transactionId: `txn_${Date.now()}` };
      }
    );
  }, [executeCriticalTask]);

  const isFinancialCritical = useCallback((workflowType: string) => {
    const classification = classifyWorkflow(workflowType);
    return classification?.level === 'critical' && classification.name.includes('Financial');
  }, [classifyWorkflow]);

  return {
    processPayment,
    isFinancialCritical,
    checkoutFinancialTask: checkoutTask,
    releaseFinancialTask: releaseTask
  };
};

// Helper functions
function generateDeviceId(): string {
  // Generate a unique device ID based on browser fingerprint
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx!.textBaseline = 'top';
  ctx!.font = '14px Arial';
  ctx!.fillText('Device fingerprint', 2, 2);
  
  const fingerprint = canvas.toDataURL();
  const hash = btoa(fingerprint).slice(0, 16);
  
  return `device_${hash}_${Date.now()}`;
}

function generateSessionId(): string {
  // Generate client-side session ID for tracking critical operations
  return `ses_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
}